/**
 * Models (`/personas`) — dezelfde DataGrid-kolommen, filters en knoppen als
 * de huidige bundle.
 *
 * Endpoint: /api/admin/models/personas (zonder `split`, dus de samengevoegde
 * lijst: 23 rijen waarvan 22 actief). De pagina toont standaard alleen
 * gekoppelde/actieve modellen; de checkbox zet de rest erbij, met het aantal
 * ertussen haakjes — precies zoals de oude pagina.
 */
import { useMemo, useState } from 'react'
import { Avatar, Empty, ErrorNote, Loading, Panel, Seg, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { Modal, Field } from '../components/Modal'
import { api } from '../lib/api'
import { num } from '../lib/format'
import { isBeheer, useMij } from '../lib/rechten'
import { useNavigate } from 'react-router-dom'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'
import { vergeetModellen } from '../lib/modellen'

export interface Persona {
  id: number
  slug: string
  name: string
  avatar_url: string | null
  stage_name: string | null
  active: boolean
  posting_hidden: boolean
  /** 07-10-2026 (Jordy): verborgen voor alle rollen in lijsten en filters. */
  hidden?: boolean
  fancentro_model_id: string | null
  f2f_model_id: string | null
  conversation_count: number
  unanswered_count: number
  chatter_count: number
  notes: string | null
  persona_slugs: string[] | null
  conversation_count_by_slug: Record<string, { total: number; unanswered: number }> | null
  persona_stage_names_by_slug: Record<string, string> | null
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]

const COLS = [
  { key: 'id', label: 'ID' },
  { key: 'stage', label: 'Stage Name' },
  { key: 'active', label: 'Active' },
  { key: 'plat', label: 'Platform ID' },
  { key: 'convos', label: 'Convos', num: true },
  { key: 'new', label: 'New', num: true },
  { key: 'chatters', label: 'Chatters', num: true },
  { key: 'notes', label: 'Notes' },
  { key: 'hidden', label: 'Hidden' },
  { key: 'actions', label: 'Actions' },
]

/** "FC: 123 · F2F: naam" — zelfde weergave als de oude kolom. */
function platformId(p: Persona): string {
  const parts: string[] = []
  if (p.fancentro_model_id) parts.push(`FC: ${p.fancentro_model_id}`)
  if (p.f2f_model_id) parts.push(`F2F: ${p.f2f_model_id}`)
  return parts.join(' · ') || '—'
}

function isLinked(p: Persona): boolean {
  return !!(p.fancentro_model_id || p.f2f_model_id)
}

export default function Models() {
  /* 07-10-2026 (Jordy): verborgen modellen alleen zichtbaar voor owner en
     admin, met 'Show hidden'; de server negeert het voor andere rollen. */
  const mij = useMij().data
  const eigenaar = ['owner', 'admin'].includes(String(mij?.role ?? '').toLowerCase())
  const [toonVerborgen, setToonVerborgen] = useState(false)
  const q = useApi<Persona[]>(
    `/api/admin/models/personas${eigenaar && toonVerborgen ? '?include_hidden=true' : ''}`)
  const [platform, setPlatform] = useState('all')
  const [search, setSearch] = useState('')
  const [showHidden, setShowHidden] = useState(false)
  const [form, setForm] = useState<Partial<Persona> | null>(null)
  const [trashOpen, setTrashOpen] = useState(false)
  /* 30-09-2026 (Jordy): hoeveel chatters er aan een model hangen, de
     prullenbak en bewerken zijn voor beheer. */
  const beheer = isBeheer(mij)
  const kolommen = COLS.filter((c) =>
    (beheer || (c.key !== 'chatters' && c.key !== 'actions')) && (eigenaar || c.key !== 'hidden'))
  const navigate = useNavigate()
  const { run, busy } = useActions()

  const all = q.data ?? []
  // Het getal achter de checkbox is het aantal dat nú verborgen wordt.
  const hiddenCount = useMemo(
    () => all.filter((p) => !p.active || !isLinked(p)).length,
    [all],
  )

  // 04-10-2026 (Jordy): filter op één model, zoals op de andere pagina's.
  const filter = useModelFilter()
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return all.filter((p) => {
      if (!filter.past(p.slug)) return false
      // Een verborgen model staat er alleen bij 'Show hidden', ook als het
      // inactief of ongekoppeld is.
      if (!showHidden && !p.hidden && (!p.active || !isLinked(p))) return false
      if (platform === 'fancentro' && !p.fancentro_model_id) return false
      if (platform === 'f2f' && !p.f2f_model_id) return false
      if (!needle) return true
      return (
        String(p.stage_name ?? '').toLowerCase().includes(needle) ||
        String(p.slug ?? '').toLowerCase().includes(needle) ||
        String(p.id).includes(needle)
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, platform, search, showHidden, filter.waarde, filter.modellen])

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <ModelFilter filter={filter} />
        <label className="chkrow" style={{ marginLeft: 'auto' }}>
          <span
            className={showHidden ? 'chk on' : 'chk'}
            onClick={() => setShowHidden((v) => !v)}
          >
            {showHidden ? '✓' : ''}
          </span>
          Show unlinked / inactive ({hiddenCount})
        </label>
        {eigenaar ? (
          <label className="chkrow" style={{ cursor: 'pointer' }}
                 onClick={() => setToonVerborgen((v) => !v)}>
            <span className={toonVerborgen ? 'chk on' : 'chk'}>
              {toonVerborgen ? '✓' : ''}
            </span>
            Show hidden
          </label>
        ) : null}
        {beheer ? (
          <button className="btn ghost sm" type="button" onClick={() => setTrashOpen(true)}>
            🗑 Trash
          </button>
        ) : null}
        <input
          className="inp"
          style={{ maxWidth: 200 }}
          placeholder="Search models…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="btn primary" type="button"
                onClick={() => setForm({ name: '', slug: '', stage_name: '', active: true })}>
          + Create
        </button>
      </div>

      <Panel
        title={`${num(all.length)} models`}
        flush
        right={
          <span className="mut2" style={{ fontSize: 12 }}>
            1–{all.length} of {all.length}
          </span>
        }
      >
        {q.loading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> :
          !rows.length ? <Empty>No models match this filter</Empty> : (
          <Table cols={kolommen}>
            {rows.map((p) => (
              /* Klik op de rij opent het modeldetail, zoals oud. */
              <tr key={p.id} className="clickrow"
                  onClick={() => navigate(`/personas/show/${p.id}`)}>
                <td className="mut2 mono">{p.id}</td>
                <td>
                  <b><Avatar name={p.stage_name || p.slug} src={p.avatar_url} />{p.stage_name || p.slug}</b>
                  <div className="mut2" style={{ fontSize: 12, marginLeft: 34 }}>{p.slug}</div>
                </td>
                <td>
                  {p.active
                    ? <Tag kind="ok">Active</Tag>
                    : <Tag kind="mut">Inactive</Tag>}
                </td>
                <td className="mut mono" style={{ fontSize: 12 }}>{platformId(p)}</td>
                <td className="num mono">{num(p.conversation_count)}</td>
                <td className="num">
                  {p.unanswered_count
                    ? <Tag kind="acc">{p.unanswered_count}</Tag>
                    : <span className="dim">0</span>}
                </td>
                {beheer ? <td className="num mono">{num(p.chatter_count)}</td> : null}
                <td className={p.notes ? 'mut' : 'dim'}>{p.notes || '—'}</td>
                {eigenaar ? (
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className={p.hidden ? 'btn primary sm' : 'btn ghost sm'}
                      type="button"
                      title={p.hidden
                        ? 'Hidden for everyone. Click to show this model again.'
                        : 'Hide this model for everyone (data keeps coming in).'}
                      disabled={busy === `Hidden ${p.id}`}
                      onClick={() => run({
                        label: `Hidden ${p.id}`,
                        confirm: p.hidden
                          ? `Show ${p.stage_name || p.slug} again for everyone?`
                          : `Hide ${p.stage_name || p.slug} for everyone? Chats, fans, content and stats stay saved and come back when you show it again.`,
                        request: mut.post(`/api/admin/models/personas/${p.id}/hidden`, { hidden: !p.hidden }),
                        onDone: () => { vergeetModellen(); q.refetch() },
                        successText: p.hidden ? 'Model visible again.' : 'Model hidden.',
                      })}
                    >
                      {p.hidden ? 'Hidden ✓' : 'Hide'}
                    </button>
                  </td>
                ) : null}
                {beheer ? (
                  <td><button className="btn ghost sm" type="button"
                              onClick={() => setForm(p)}>Edit</button></td>
                ) : null}
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      {form ? (
        <Modal
          title={form.id ? `Edit model — ${form.stage_name || form.slug}` : 'Create model'}
          onClose={() => setForm(null)}
          footer={
            <>
              <button className="btn ghost sm" type="button" onClick={() => setForm(null)}>
                Cancel
              </button>
              <button
                className="btn primary sm"
                type="button"
                disabled={busy === 'Save model'}
                onClick={() => run({
                  label: 'Save model',
                  request: () => form.id
                    ? api.post(`/api/admin/models/personas/${form.id}`, form)
                    : api.post('/api/admin/models/personas', form),
                  onDone: () => { setForm(null); q.refetch() },
                  successText: 'Model saved.',
                })}
              >
                Save
              </button>
            </>
          }
        >
          <Field label="Stage name">
            <input className="inp" value={form.stage_name ?? ''}
                   onChange={(e) => setForm((f) => ({ ...f!, stage_name: e.target.value }))} />
          </Field>
          <Field label="Slug" hint="Internal name, e.g. fc_aliceimonta">
            <input className="inp" value={form.slug ?? ''}
                   onChange={(e) => setForm((f) => ({ ...f!, slug: e.target.value }))} />
          </Field>
          <Field label="FanCentro model-id">
            <input className="inp" value={form.fancentro_model_id ?? ''}
                   onChange={(e) => setForm((f) => ({ ...f!, fancentro_model_id: e.target.value }))} />
          </Field>
          <Field label="F2F model-id">
            <input className="inp" value={form.f2f_model_id ?? ''}
                   onChange={(e) => setForm((f) => ({ ...f!, f2f_model_id: e.target.value }))} />
          </Field>
          <Field label="Notities">
            <textarea className="inp" value={form.notes ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f!, notes: e.target.value }))} />
          </Field>
          <label className="chkrow">
            <span className={form.active ? 'chk on' : 'chk'}
                  onClick={() => setForm((f) => ({ ...f!, active: !f!.active }))}>
              {form.active ? '✓' : ''}
            </span>
            Active
          </label>
          <label className="chkrow">
            <span className={form.posting_hidden ? 'chk on' : 'chk'}
                  onClick={() => setForm((f) => ({ ...f!, posting_hidden: !f!.posting_hidden }))}>
              {form.posting_hidden ? '✓' : ''}
            </span>
            Hide on the Posting Bot page
          </label>
        </Modal>
      ) : null}

      {trashOpen ? <TrashDialog onClose={() => setTrashOpen(false)} onChanged={q.refetch} /> : null}
    </>
  )
}

/** Prullenbak: verwijderde persona's terugzetten (POST models/trash/personas). */
function TrashDialog({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const t = useApi<Persona[] | { data: Persona[] }>('/api/admin/models/trash/personas')
  const rows = Array.isArray(t.data) ? t.data : (t.data?.data ?? [])
  const { run, busy } = useActions()
  return (
    <Modal title="Prullenbak" width={640} onClose={onClose}>
      {t.loading ? <Loading /> : !rows.length ? <Empty>Prullenbak is leeg</Empty> : (
        <Table cols={[
          { key: 'id', label: 'ID' }, { key: 'n', label: 'Model' }, { key: 'a', label: '' },
        ]}>
          {rows.map((p) => (
            <tr key={p.id}>
              <td className="mut2 mono">{p.id}</td>
              <td>{p.stage_name || p.slug}</td>
              <td>
                <button
                  className="btn ghost sm"
                  type="button"
                  disabled={busy === 'Terugzetten'}
                  onClick={() => run({
                    label: 'Terugzetten',
                    // Terugzetten gaat per persona: /trash/personas/{id}/restore.
                    // Het kale pad kent alleen GET en gaf dus 405.
                    request: mut.post(`/api/admin/models/trash/personas/${p.id}/restore`),
                    onDone: () => { t.refetch(); onChanged() },
                    successText: `${p.stage_name || p.slug} teruggezet`,
                  })}
                >
                  Terugzetten
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Modal>
  )
}
